import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Flame, ChevronRight, X } from "lucide-react";

const STATUSES = ["NEW", "CONTACTED", "QUALIFIED", "CALL_BOOKED", "FOUNDING_CANDIDATE", "NURTURE", "CONVERTED", "CLOSED"];
const TEMP_STYLE = { HOT: "bg-red-100 text-red-700", WARM: "bg-amber-100 text-amber-700", STANDARD: "bg-slate-100 text-slate-600" };

// Platform Super-Admin only (route is SuperProtected). Presents Jan-2027 early-access leads with
// search/filters, status management and internal notes. Never exposes customer-organisation data.
export function EarlyAccessLeads() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/early-access/admin/leads", { params });
      setLeads(data.leads || []);
    } catch { toast.error("Could not load leads"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div data-testid="early-access-leads">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input placeholder="Search name, business or email…" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()} data-testid="leads-search" className="max-w-xs bg-white" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leads-status-filter"
          className="h-9 rounded-md border border-slate-200 bg-white text-sm px-2">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={load} data-testid="leads-refresh">Search</Button>
        <span className="text-xs text-slate-400 ml-auto" data-testid="leads-count">{leads.length} lead{leads.length === 1 ? "" : "s"}</span>
      </div>

      {loading ? <p className="text-sm text-slate-400">Loading…</p> :
        leads.length === 0 ? <p className="text-sm text-slate-400" data-testid="leads-empty">No early-access leads yet.</p> :
          leads.map((l) => (
            <div key={l.lead_id} className="cc-list-row" data-testid={`lead-${l.lead_id}`} onClick={() => setDetail(l)}>
              <div className="min-w-0">
                <div className="font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
                  <span className="truncate">{l.first_name} {l.last_name}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${TEMP_STYLE[l.lead_temperature] || TEMP_STYLE.STANDARD}`} data-testid={`lead-temp-${l.lead_id}`}>{l.lead_temperature} · {l.lead_score}</span>
                  {l.founding_kitchen_interest && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-brand-light text-brand-dark flex items-center gap-1"><Flame size={10} /> Founding</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{l.business_name} · {l.business_type} · {l.site_count_band} site(s) · <span className="font-medium">{l.status?.replace(/_/g, " ")}</span></div>
              </div>
              <ChevronRight size={18} className="text-slate-300 shrink-0" />
            </div>
          ))}

      {detail && <LeadDetail lead={detail} onClose={() => setDetail(null)} onSaved={(u) => { setDetail(u); load(); }} />}
    </div>
  );
}

function LeadDetail({ lead, onClose, onSaved }) {
  const [status, setStatus] = useState(lead.status);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      if (status !== lead.status) body.status = status;
      if (note.trim()) body.note = note.trim();
      const { data } = await api.patch(`/early-access/admin/leads/${lead.lead_id}`, body);
      toast.success("Lead updated");
      setNote("");
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const Row = ({ k, v }) => <div className="flex gap-2 text-sm py-1 border-b border-slate-50"><span className="w-36 text-slate-400 shrink-0">{k}</span><span className="text-slate-800 break-words">{v || "—"}</span></div>;

  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="lead-detail">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold text-slate-800">{lead.first_name} {lead.last_name}</h3>
          <button onClick={onClose} data-testid="lead-detail-close" className="text-slate-400"><X size={18} /></button>
        </div>
        <Row k="Business" v={lead.business_name} />
        <Row k="Email" v={lead.email} />
        <Row k="Phone" v={lead.phone} />
        <Row k="Role" v={lead.role} />
        <Row k="Business type" v={lead.business_type} />
        <Row k="Sites" v={lead.site_count_band} />
        <Row k="Employees" v={lead.employee_count_band} />
        <Row k="Current system" v={lead.current_system} />
        <Row k="Pain point" v={lead.pain_point} />
        <Row k="Interests" v={(lead.interest_areas || []).join(", ")} />
        <Row k="Founding kitchen" v={lead.founding_kitchen_interest ? "Yes" : "No"} />
        <Row k="Marketing consent" v={lead.marketing_consent ? `Yes (${lead.marketing_consent_at || ""})` : "No"} />
        <Row k="Score" v={`${lead.lead_score} · ${lead.lead_temperature}`} />
        <Row k="Source" v={lead.source} />
        <Row k="UTM" v={["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].map((u) => lead[u]).filter(Boolean).join(" / ")} />
        <Row k="Landing" v={lead.landing_path} />
        <Row k="Created" v={lead.created_at} />

        {(lead.internal_notes || []).length > 0 && (
          <div className="mt-3" data-testid="lead-notes">
            <div className="text-xs font-semibold text-slate-500 mb-1">Internal notes</div>
            {lead.internal_notes.map((n, i) => <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded p-2 mb-1">{n.note} <span className="text-slate-400">— {n.by}, {n.at}</span></div>)}
          </div>
        )}

        <div className="mt-4 space-y-2">
          <label className="text-xs font-semibold text-slate-500 block">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lead-status-select"
            className="w-full h-9 rounded-md border border-slate-200 bg-white text-sm px-2">
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <Input placeholder="Add an internal note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lead-note-input" />
          <Button onClick={save} disabled={busy} data-testid="lead-save" className="w-full bg-brand hover:bg-brand-hover">Save</Button>
        </div>
      </div>
    </div>
  );
}
